// TransportLayerOverlay — imperatively manages MapLibre transport line layers.
// Renders nothing to the DOM; all work is done via the MapLibre imperative API.
// REQ-6.8 / add-transport-map-layers.
import * as React from 'react';
import type { MapInstance } from '@ooh/ui/components/map-view';
import {
  TRANSPORT_SOURCE_ID,
  TRANSPORT_PUBLIC_OUTLINE_LAYER_ID,
  TRANSPORT_PUBLIC_LAYER_ID,
  TRANSPORT_ROADS_OUTLINE_LAYER_ID,
  TRANSPORT_ROADS_LAYER_ID,
  CITY_ZOOM_THRESHOLD,
  ROAD_ZOOM_THRESHOLD,
  ROAD_OUTLINE_COLOR,
  ROAD_LINE_COLOR,
  PUBLIC_OUTLINE_COLOR,
  MODE_TYPE_I18N_KEY,
  MODE_TYPE_UNKNOWN_I18N_KEY,
  MODE_COLORS,
  MODE_COLOR_FALLBACK,
  MODE_DASH_ARRAYS,
  MODE_DASH_ARRAY_FALLBACK,
  PIN_CLUSTER_LAYER_ID,
} from '../constants';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LinePopupState {
  x: number;
  y: number;
  label: string;
  typeLabel: string;
}

export type TransportGeoJson = {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    id?: string | number;
    geometry:
      | { type: 'LineString'; coordinates: number[][] }
      | { type: 'MultiLineString'; coordinates: number[][][] };
    properties: { scope: string; mode: string; label: string };
  }>;
};

export interface TransportLayerOverlayProps {
  mapInstance: MapInstance | null;
  geoJson: TransportGeoJson;
  isPublicEnabled: boolean;
  isRoadsEnabled: boolean;
  isLoading: boolean;
  isEmpty: boolean;
  onLineClick: (state: LinePopupState) => void;
}

// ─── Layer builders ───────────────────────────────────────────────────────────

function ensureSource(map: MapInstance, geoJson: TransportGeoJson): void {
  if (!map.getSource(TRANSPORT_SOURCE_ID)) {
    // Cast required: MapLibre's addSource types 'geojson' data as GeoJSON.GeoJsonObject, but our
    // TransportGeoJson is a locally-typed FeatureCollection not assignable to the imported type.
    map.addSource(TRANSPORT_SOURCE_ID, { type: 'geojson', data: geoJson as never });
  }
}

function ensureRoadsLayers(map: MapInstance, beforeId: string | undefined): void {
  if (!map.getLayer(TRANSPORT_ROADS_OUTLINE_LAYER_ID)) {
    map.addLayer(
      {
        id: TRANSPORT_ROADS_OUTLINE_LAYER_ID,
        type: 'line',
        source: TRANSPORT_SOURCE_ID,
        filter: ['==', ['get', 'scope'], 'roads'],
        paint: {
          'line-color': ROAD_OUTLINE_COLOR,
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 4, 9, 7],
          'line-opacity': 0.9,
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        // Cast required: MapLibre's addLayer expects a typed LayerSpecification union; our plain
        // object with interpolate/match expressions is not assignable without the cast.
      } as never,
      beforeId,
    );
  }
  if (!map.getLayer(TRANSPORT_ROADS_LAYER_ID)) {
    map.addLayer(
      {
        id: TRANSPORT_ROADS_LAYER_ID,
        type: 'line',
        source: TRANSPORT_SOURCE_ID,
        filter: ['==', ['get', 'scope'], 'roads'],
        paint: {
          'line-color': ROAD_LINE_COLOR,
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 2, 9, 4],
          'line-opacity': 0.9,
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        // Cast required: same LayerSpecification union constraint as above.
      } as never,
      beforeId,
    );
  }
}

/**
 * Build a MapLibre 'match' expression for line-color driven by MODE_COLORS / MODE_COLOR_FALLBACK.
 * Excludes the 'road' key (roads scope uses its own layer).
 */
function buildModeColorExpression(): unknown[] {
  const publicModes = Object.entries(MODE_COLORS).filter(([mode]) => mode !== 'road');
  const matchEntries = publicModes.flatMap(([mode, color]) => [mode, color]);
  return ['match', ['get', 'mode'], ...matchEntries, MODE_COLOR_FALLBACK];
}

/**
 * Build a MapLibre 'match' expression for line-dasharray driven by MODE_DASH_ARRAYS.
 * If a mode is renamed in TransportFeatureModeSchema, the expression updates automatically.
 */
function buildModeDashExpression(): unknown[] {
  const matchEntries = Object.entries(MODE_DASH_ARRAYS).flatMap(([mode, dash]) => [
    mode,
    ['literal', dash],
  ]);
  return ['match', ['get', 'mode'], ...matchEntries, ['literal', MODE_DASH_ARRAY_FALLBACK]];
}

function ensurePublicLayers(map: MapInstance, beforeId: string | undefined): void {
  if (!map.getLayer(TRANSPORT_PUBLIC_OUTLINE_LAYER_ID)) {
    map.addLayer(
      {
        id: TRANSPORT_PUBLIC_OUTLINE_LAYER_ID,
        type: 'line',
        source: TRANSPORT_SOURCE_ID,
        filter: ['==', ['get', 'scope'], 'public'],
        paint: {
          'line-color': PUBLIC_OUTLINE_COLOR,
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 3, 10, 5],
          'line-opacity': 0.95,
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        // Cast required: same LayerSpecification union constraint as road layers above.
      } as never,
      beforeId,
    );
  }
  if (!map.getLayer(TRANSPORT_PUBLIC_LAYER_ID)) {
    map.addLayer(
      {
        id: TRANSPORT_PUBLIC_LAYER_ID,
        type: 'line',
        source: TRANSPORT_SOURCE_ID,
        filter: ['==', ['get', 'scope'], 'public'],
        paint: {
          // Derived from MODE_COLORS / MODE_COLOR_FALLBACK so changes in constants propagate here.
          'line-color': buildModeColorExpression(),
          'line-width': ['interpolate', ['linear'], ['zoom'], 5, 2.5, 10, 4.5],
          'line-opacity': 1,
          // Derived from MODE_DASH_ARRAYS so renaming a mode in constants propagates here.
          'line-dasharray': buildModeDashExpression(),
        },
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        // Cast required: same LayerSpecification union constraint as road layers above.
      } as never,
      beforeId,
    );
  }
}

function getBeforeId(map: MapInstance): string | undefined {
  return map.getLayer(PIN_CLUSTER_LAYER_ID) ? PIN_CLUSTER_LAYER_ID : undefined;
}

function setLayerVisibility(map: MapInstance, layerId: string, visible: boolean): void {
  if (map.getLayer(layerId)) {
    map.setLayoutProperty(layerId, 'visibility', visible ? 'visible' : 'none');
  }
}

type ClickEvent = {
  point: { x: number; y: number };
  features?: Array<{ properties: Record<string, unknown> | null }>;
};

interface SyncLayersOptions {
  map: MapInstance;
  geoJson: TransportGeoJson;
  isPublicEnabled: boolean;
  isRoadsEnabled: boolean;
  showPublic: boolean;
  showRoads: boolean;
}

function syncTransportLayers({
  map,
  geoJson,
  isPublicEnabled,
  isRoadsEnabled,
  showPublic,
  showRoads,
}: SyncLayersOptions): void {
  const anyEnabled = isPublicEnabled || isRoadsEnabled;

  if (anyEnabled) {
    ensureSource(map, geoJson);
    const beforeId = getBeforeId(map);
    if (isRoadsEnabled) ensureRoadsLayers(map, beforeId);
    if (isPublicEnabled) ensurePublicLayers(map, beforeId);
  }

  setLayerVisibility(map, TRANSPORT_ROADS_OUTLINE_LAYER_ID, showRoads);
  setLayerVisibility(map, TRANSPORT_ROADS_LAYER_ID, showRoads);
  setLayerVisibility(map, TRANSPORT_PUBLIC_OUTLINE_LAYER_ID, showPublic);
  setLayerVisibility(map, TRANSPORT_PUBLIC_LAYER_ID, showPublic);

  const source = map.getSource(TRANSPORT_SOURCE_ID) as
    | { setData: (d: TransportGeoJson) => void }
    | undefined;
  source?.setData(geoJson);
}

function makeClickHandler(
  enabled: boolean,
  onLineClick: (state: LinePopupState) => void,
): (e: ClickEvent) => void {
  return (e: ClickEvent) => {
    if (!enabled) return;
    const feat = e.features?.[0];
    if (!feat) return;
    const props = feat.properties ?? {};
    const mode = String(props.mode ?? '');
    onLineClick({
      x: e.point.x,
      y: e.point.y,
      label: String(props.label ?? 'Linia'),
      // Store i18n key — caller must translate with t() from the 'map' namespace.
      // Cast is safe: unknown modes fall through to the ?? fallback.
      typeLabel:
        MODE_TYPE_I18N_KEY[mode as keyof typeof MODE_TYPE_I18N_KEY] ?? MODE_TYPE_UNKNOWN_I18N_KEY,
    });
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TransportLayerOverlay({
  mapInstance,
  geoJson,
  isPublicEnabled,
  isRoadsEnabled,
  isLoading,
  isEmpty,
  onLineClick,
}: TransportLayerOverlayProps): null {
  // Track zoom state
  const [isCityZoom, setIsCityZoom] = React.useState(false);
  const [isRegionalZoom, setIsRegionalZoom] = React.useState(false);

  // Keep stable ref to onLineClick to avoid re-registering click handlers on every render
  const onLineClickRef = React.useRef(onLineClick);
  React.useEffect(() => {
    onLineClickRef.current = onLineClick;
  }, [onLineClick]);

  // ─── Zoom tracking ────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (mapInstance === null) return;

    const updateZoom = () => {
      const z = mapInstance.getZoom();
      setIsCityZoom(z >= CITY_ZOOM_THRESHOLD);
      setIsRegionalZoom(z >= ROAD_ZOOM_THRESHOLD);
    };

    updateZoom();
    mapInstance.on('zoomend', updateZoom);
    mapInstance.on('moveend', updateZoom);

    return () => {
      mapInstance.off('zoomend', updateZoom);
      mapInstance.off('moveend', updateZoom);
    };
  }, [mapInstance]);

  // ─── Layer management ─────────────────────────────────────────────────────

  React.useEffect(() => {
    if (mapInstance === null) return;

    const showPublic = isPublicEnabled && !isLoading && !isEmpty && isCityZoom;
    const showRoads = isRoadsEnabled && isRegionalZoom;

    syncTransportLayers({
      map: mapInstance,
      geoJson,
      isPublicEnabled,
      isRoadsEnabled,
      showPublic,
      showRoads,
    });
  }, [
    mapInstance,
    geoJson,
    isPublicEnabled,
    isRoadsEnabled,
    isLoading,
    isEmpty,
    isCityZoom,
    isRegionalZoom,
  ]);

  // ─── Hover cursors ────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (mapInstance === null) return;

    const setCursor = () => {
      mapInstance.getCanvas().style.cursor = 'pointer';
    };
    const resetCursor = () => {
      mapInstance.getCanvas().style.cursor = '';
    };

    const hoverLayers = [TRANSPORT_ROADS_LAYER_ID, TRANSPORT_PUBLIC_LAYER_ID];
    hoverLayers.forEach((lid) => {
      if (mapInstance.getLayer(lid)) {
        mapInstance.on('mouseenter', lid, setCursor);
        mapInstance.on('mouseleave', lid, resetCursor);
      }
    });

    return () => {
      // Guard against MapLibre internal state being destroyed before cleanup runs
      // (e.g. when the map unmounts while navigating away from the map tab).
      try {
        hoverLayers.forEach((lid) => {
          if (mapInstance.getLayer(lid)) {
            mapInstance.off('mouseenter', lid, setCursor);
            mapInstance.off('mouseleave', lid, resetCursor);
          }
        });
      } catch {
        // Map already removed — nothing to clean up
      }
    };
  }, [mapInstance]);

  // ─── Click handlers ───────────────────────────────────────────────────────

  React.useEffect(() => {
    if (mapInstance === null) return;

    const showPublic = isPublicEnabled && !isLoading && !isEmpty && isCityZoom;
    const showRoads = isRoadsEnabled && isRegionalZoom;

    // Cast required: MapLibre's map.on('click', layer, handler) expects a MapLayerMouseEvent
    // handler; our local ClickEvent type captures only the fields we use and is not assignable.
    const onRoadClick = makeClickHandler(showRoads, (state) =>
      onLineClickRef.current(state),
    ) as never;
    // Cast required: same MapLayerMouseEvent handler constraint as onRoadClick above.
    const onPublicClick = makeClickHandler(showPublic, (state) =>
      onLineClickRef.current(state),
    ) as never;

    if (mapInstance.getLayer(TRANSPORT_ROADS_LAYER_ID)) {
      mapInstance.on('click', TRANSPORT_ROADS_LAYER_ID, onRoadClick);
    }
    if (mapInstance.getLayer(TRANSPORT_PUBLIC_LAYER_ID)) {
      mapInstance.on('click', TRANSPORT_PUBLIC_LAYER_ID, onPublicClick);
    }

    return () => {
      try {
        if (mapInstance.getLayer(TRANSPORT_ROADS_LAYER_ID)) {
          mapInstance.off('click', TRANSPORT_ROADS_LAYER_ID, onRoadClick);
        }
        if (mapInstance.getLayer(TRANSPORT_PUBLIC_LAYER_ID)) {
          mapInstance.off('click', TRANSPORT_PUBLIC_LAYER_ID, onPublicClick);
        }
      } catch {
        // Map already removed — nothing to clean up
      }
    };
  }, [
    mapInstance,
    isPublicEnabled,
    isRoadsEnabled,
    isLoading,
    isEmpty,
    isCityZoom,
    isRegionalZoom,
  ]);

  return null;
}
